"use client";

import Button from "@/components/ui/Button/Button";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import { useState } from "react";

// Helper to check if user has an active team
const hasActiveTeam = (session) => {
    if (!session?.user) return false;

    // Check currentTeam (active or trialing)
    if (
        session.user.currentTeam &&
        (session.user.currentTeam.teamStatus === "active" ||
            session.user.currentTeam.teamStatus === "trialing")
    ) {
        return true;
    }

    // Check availableTeams
    if (session.user.availableTeams?.length > 0) {
        return session.user.availableTeams.some(
            (team) =>
                team.teamStatus === "active" || team.teamStatus === "trialing",
        );
    }

    return false;
};

export default function PlanSwitcher() {
    const { data: session } = useSession();
    const t = useTranslations("PlanSwitcher");
    const [selectedPlan, setSelectedPlan] = useState("individual");

    // Don't show if user is pro or has an active/trialing team
    if (session?.user?.pro || hasActiveTeam(session)) {
        return null;
    }

    return (
        <div className="border border-gray-200/60 dark:border-grayDark-700/50 bg-white/80 dark:bg-grayDark-900/80 backdrop-blur-sm p-4 rounded-2xl mt-4 shadow-sm">
            {/* Switcher */}
            <div className="relative flex gap-3 bg-gray-50 dark:bg-grayDark-800/60 rounded-xl p-0.5 w-full max-w-md mx-auto">
                {/* Individual */}
                <button
                    onClick={() => setSelectedPlan("individual")}
                    className={`relative flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex-1 group border ${
                        selectedPlan === "individual"
                            ? "border-confidence text-confidence dark:border-confidence-100 dark:text-confidence-100 bg-confidence/5"
                            : "border-gray-200 dark:border-grayDark-700 text-gray-600 dark:text-grayDark-400 hover:bg-gray-100 dark:hover:border-grayDark-600 dark:hover:bg-grayDark-800"
                    }`}
                >
                    <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <span className="tracking-wide">
                        {t("individual.label")}
                    </span>
                </button>

                {/* Team */}
                <button
                    onClick={() => setSelectedPlan("team")}
                    className={`relative flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex-1 group border ${
                        selectedPlan === "team"
                            ? "border-confidence text-confidence dark:text-confidence bg-confidence/5"
                            : "border-gray-200 dark:border-grayDark-700 text-gray-600 dark:text-grayDark-400 hover:bg-gray-100 dark:hover:border-grayDark-600 dark:hover:bg-grayDark-800"
                    }`}
                >
                    <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                    <span className="tracking-wide">{t("team.label")}</span>
                </button>
            </div>

            {selectedPlan === "individual" && (
                <div className="mt-4">
                    <p className="text-gray-700 dark:text-grayDark-300 font-semibold">
                        {t("individual.title")}
                    </p>
                    <div className="flex flex-col gap-2 text-[#77777c] dark:text-grayDark-400">
                        <div className="flex gap-2 text-sm">
                            <div className="w-5 h-5 flex items-center justify-center">
                                <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M14.217 3.5a5.17 5.17 0 0 0-4.434 0L5.489 5.512a2.25 2.25 0 0 1 .647 4.306l-1.076.461c-.534.23-.837.362-1.042.467l-.003.05L9.783 13.5a5.17 5.17 0 0 0 4.434 0l6.691-3.137c1.456-.682 1.456-3.044 0-3.726z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M5 12.915v3.71c0 1.008.503 1.952 1.385 2.44C7.854 19.88 10.204 21 12 21s4.146-1.121 5.615-1.934c.881-.489 1.385-1.433 1.385-2.44v-3.711l-4.146 1.944a6.67 6.67 0 0 1-5.708 0zm.545-4.475a.75.75 0 0 0-.59-1.38l-1.112.477c-.557.239-1.03.441-1.4.65c-.395.222-.734.482-.989.868c-.254.386-.36.8-.408 1.25C1 10.729 1 11.243 1 11.85v2.901a.75.75 0 0 0 1.5 0v-2.862c0-.656.001-1.088.037-1.421c.034-.315.093-.47.17-.586c.075-.115.195-.231.471-.387c.292-.164.689-.335 1.292-.593z"
                                    />
                                </svg>
                            </div>
                            <div>
                                {t("individual.all_formations_in_unlimited")}
                            </div>
                        </div>
                        <div className="flex gap-2 text-sm">
                            <div className="w-5 h-5 flex items-center justify-center">
                                <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    >
                                        <path d="M11.146 7.023C11.526 6.34 11.716 6 12 6s.474.34.854 1.023l.098.176c.108.194.162.29.246.354c.085.064.19.088.4.135l.19.044c.738.167 1.107.25 1.195.532s-.164.577-.667 1.165l-.13.152c-.143.167-.215.25-.247.354s-.021.215 0 .438l.02.203c.076.785.114 1.178-.115 1.352c-.23.174-.576.015-1.267-.303l-.178-.082c-.197-.09-.295-.135-.399-.135s-.202.045-.399.135l-.178.082c-.691.319-1.037.477-1.267.303s-.191-.567-.115-1.352l.02-.203c.021-.223.032-.334 0-.438s-.104-.187-.247-.354l-.13-.152c-.503-.588-.755-.882-.667-1.165c.088-.282.457-.365 1.195-.532l.19-.044c.21-.047.315-.07.4-.135c.084-.064.138-.16.246-.354z" />
                                        <path d="M19 9A7 7 0 1 1 5 9a7 7 0 0 1 14 0Z" />
                                        <path d="m12 16.068l-3.771 3.905c-.54.56-.81.839-1.04.935c-.52.22-1.099.032-1.373-.448c-.12-.21-.158-.59-.232-1.35c-.043-.43-.064-.644-.128-.824a1.43 1.43 0 0 0-.835-.864c-.173-.067-.38-.088-.795-.132c-.734-.078-1.101-.117-1.305-.241c-.463-.284-.646-.883-.433-1.422c.094-.237.364-.517.904-1.076L5.456 12M12 16.068l3.771 3.905c.54.56.81.839 1.04.935c.52.22 1.099.032 1.373-.448c.12-.21.157-.59.232-1.35c.043-.43.064-.644.128-.824c.144-.402.446-.715.835-.864c.173-.067.38-.088.795-.132c.734-.078 1.101-.117 1.305-.241c.463-.284.646-.883.433-1.422c-.094-.237-.364-.517-.904-1.076L18.544 12" />
                                    </g>
                                </svg>
                            </div>
                            <div>{t("individual.access_to_all_projects")}</div>
                        </div>
                        <div className="flex gap-2 text-sm">
                            <div className="w-5 h-5 flex items-center justify-center">
                                <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <defs>
                                        <mask id="solarChatRoundDotsBold0">
                                            <g fill="none">
                                                <path
                                                    fill="#fff"
                                                    d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12c0 1.6.376 3.112 1.043 4.453c.178.356.237.763.134 1.148l-.595 2.226a1.3 1.3 0 0 0 1.591 1.592l2.226-.596a1.63 1.63 0 0 1 1.149.133A9.96 9.96 0 0 0 12 22"
                                                />
                                                <path
                                                    fill="#000"
                                                    d="M15 12a1 1 0 1 0 2 0a1 1 0 0 0-2 0m-4 0a1 1 0 1 0 2 0a1 1 0 0 0-2 0m-4 0a1 1 0 1 0 2 0a1 1 0 0 0-2 0"
                                                />
                                            </g>
                                        </mask>
                                    </defs>
                                    <path
                                        fill="currentColor"
                                        d="M0 0h24v24H0z"
                                        mask="url(#solarChatRoundDotsBold0)"
                                    />
                                </svg>
                            </div>
                            <div>
                                {t(
                                    "individual.custom_answers_to_your_questions",
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2 text-sm mb-3">
                            <div className="w-5 h-5 flex items-center justify-center">
                                <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                    >
                                        <path d="M2 12V8c0-2.828 0-4.243.879-5.121C3.757 2 5.172 2 8 2h8c2.828 0 4.243 0 5.121.879C22 3.757 22 5.172 22 8v4c0 2.828 0 4.243-.879 5.121c-.646.647-1.582.818-3.158.863a3.2 3.2 0 0 0-.767-2.066a.2.2 0 0 1-.05-.12a3.21 3.21 0 0 0-2.944-2.945a.2.2 0 0 1-.12-.05a3.21 3.21 0 0 0-4.164 0a.2.2 0 0 1-.12.05a3.21 3.21 0 0 0-2.945 2.945a.2.2 0 0 1-.05.12a3.2 3.2 0 0 0-.766 2.066c-1.576-.045-2.512-.216-3.158-.863C2 16.243 2 14.828 2 12m6.25-6A.75.75 0 0 1 9 5.25h6a.75.75 0 0 1 0 1.5H9A.75.75 0 0 1 8.25 6M7 8.75a.75.75 0 0 0 0 1.5h10a.75.75 0 0 0 0-1.5z" />
                                        <path d="M13.11 13.945a1.71 1.71 0 0 0-2.22 0a1.7 1.7 0 0 1-.973.403a1.71 1.71 0 0 0-1.569 1.57c-.028.358-.17.698-.403.973a1.71 1.71 0 0 0 0 2.218c.234.274.375.615.403.973a1.71 1.71 0 0 0 1.57 1.57c.358.028.699.169.973.402a1.71 1.71 0 0 0 2.218 0a1.7 1.7 0 0 1 .973-.403a1.71 1.71 0 0 0 1.57-1.569a1.7 1.7 0 0 1 .402-.973a1.71 1.71 0 0 0 0-2.219a1.7 1.7 0 0 1-.403-.973a1.71 1.71 0 0 0-1.569-1.569a1.7 1.7 0 0 1-.973-.403m.902 3.603a.75.75 0 1 0-1.024-1.096l-1.63 1.522l-.346-.322a.75.75 0 0 0-1.024 1.096l.857.8a.75.75 0 0 0 1.024 0z" />
                                    </g>
                                </svg>
                            </div>
                            <div>
                                {t("individual.get_success_certifications")}
                            </div>
                        </div>
                    </div>
                    <Link href="/pricing">
                        <Button type="outline-secondary" block>
                            {t("individual.button")}
                        </Button>
                    </Link>
                </div>
            )}
            {selectedPlan === "team" && (
                <div className="mt-4">
                    <p className="text-gray-700 dark:text-grayDark-300 font-semibold">
                        {t("team.title")}
                    </p>
                    <p className="text-[#77777c] dark:text-grayDark-400 text-sm">
                        {t("team.description")}
                    </p>
                    <Link href="/teams" className="mt-1">
                        <Button type="outline-secondary" block>
                            {t("team.button")}
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
}
